/**
 * ExportButton Component
 * Button to export Model Planner data as PDF
 * Shows modal for optional notes before generating export
 */

import React, { useState } from 'react';
import { FileDown, X } from 'lucide-react';

const ExportButton = ({
  sessionData,
  project,
  disabled = false,
  onExport
}) => {
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const handleExportClick = () => {
    setShowModal(true);
  };

  const handleConfirmExport = async () => {
    setIsExporting(true);
    try {
      await onExport(notes);
      setShowModal(false);
      setNotes('');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to generate export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setNotes('');
  };

  return (
    <>
      {/* Export Button */}
      <button
        onClick={handleExportClick}
        disabled={disabled}
        title={disabled ? 'Complete at least one section to enable export' : 'Export Model Planner to PDF'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: '8px 16px',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: disabled ? 'var(--gray-500)' : 'var(--gray-200)',
          backgroundColor: disabled ? 'var(--gray-800)' : 'var(--gray-700)',
          border: `1px solid ${disabled ? 'var(--gray-700)' : 'var(--gray-600)'}`,
          borderRadius: 'var(--radius-md)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = 'var(--gray-600)';
            e.target.style.borderColor = 'var(--gray-500)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = 'var(--gray-700)';
            e.target.style.borderColor = 'var(--gray-600)';
          }
        }}
      >
        <FileDown size={16} />
        Export PDF
      </button>

      {/* Export Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={handleCancel}
        >
          <div
            style={{
              backgroundColor: 'var(--dark-surface)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-6)',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-4)'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)'
              }}>
                Export Model Planner
              </h3>
              <button
                onClick={handleCancel}
                disabled={isExporting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  padding: 0,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-400)',
                  cursor: isExporting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isExporting) {
                    e.target.style.backgroundColor = 'var(--gray-700)';
                    e.target.style.color = 'var(--gray-200)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--gray-400)';
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Project Info */}
            <div style={{
              padding: 'var(--space-3)',
              backgroundColor: 'var(--gray-800)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-4)'
            }}>
              <p style={{
                margin: 0,
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)'
              }}>
                <strong style={{ color: 'var(--gray-200)' }}>Project:</strong>{' '}
                {project?.jobNumber || 'N/A'} - {project?.name || project?.projectName || 'Untitled'}
              </p>
              <p style={{
                margin: 'var(--space-2) 0 0 0',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)'
              }}>
                <strong style={{ color: 'var(--gray-200)' }}>Export Date:</strong>{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {/* Notes Field */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--gray-200)'
              }}>
                Additional Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isExporting}
                placeholder="Add any additional notes or comments about this model planner..."
                rows={4}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-100)',
                  backgroundColor: 'var(--gray-900)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCancel}
                disabled={isExporting}
                style={{
                  padding: '10px 20px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                  backgroundColor: 'var(--gray-800)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  cursor: isExporting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isExporting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isExporting) {
                    e.target.style.backgroundColor = 'var(--gray-700)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-800)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmExport}
                disabled={isExporting}
                style={{
                  padding: '10px 20px',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'white',
                  backgroundColor: isExporting ? 'var(--gray-600)' : 'var(--cta-blue)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: isExporting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}
                onMouseEnter={(e) => {
                  if (!isExporting) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isExporting) {
                    e.target.style.backgroundColor = 'var(--cta-blue)';
                  }
                }}
              >
                {isExporting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown size={16} />
                    Generate PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ExportButton;
